import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import {
  ExternalLink, TrendingUp, TrendingDown, Minus, AlertTriangle,
  Clock, RefreshCw, Radio, Info, ChevronRight, DollarSign,
} from 'lucide-react';

const CATEGORIES = [
  'All', 'FERS', 'Markets', 'Policy', 'FEHB', 'SECURE 2.0', 'OPM Updates',
];

const IMPACT_META = {
  Positive: { icon: TrendingUp,   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  Neutral:  { icon: Minus,        cls: 'bg-gray-50 text-gray-600 border-gray-200' },
  Monitor:  { icon: AlertTriangle, cls: 'bg-amber-50 text-amber-800 border-amber-200' },
};

const TAG_PRIORITY = ['Federal', 'Retirement', 'Markets', 'Policy', 'SECURE 2.0', 'Retirement', 'FEHB', 'OPM'];

const fmtPct = (n) => `${(Number(n) || 0) >= 0 ? '+' : ''}${(Number(n) || 0).toFixed(2)}%`;

const fmtTS = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return s; }
};

// ──────────────────────────────────────────────────────────────
// Market Data Panel
// ──────────────────────────────────────────────────────────────
const MARKET_ORDER = ['S&P 500', 'NASDAQ', 'DJIA', '10-Yr Treasury', 'Fed Funds Rate', 'Gold', 'Oil WTI'];

const MarketDataPanel = () => {
  const [indices, setIndices] = useState([]);
  const [tspFunds, setTspFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [asOf, setAsOf] = useState('');

  useEffect(() => { load(); const id = setInterval(load, 5 * 60 * 1000); return () => clearInterval(id); }, []);

  const load = async () => {
    try {
      const [i, t] = await Promise.allSettled([
        api.get('/market/indices'),
        api.get('/market/tsp-funds'),
      ]);
      if (i.status === 'fulfilled') setIndices(Array.isArray(i.value.data) ? i.value.data : (i.value.data?.indices || []));
      if (t.status === 'fulfilled') setTspFunds(Array.isArray(t.value.data) ? t.value.data : []);
      setAsOf(new Date().toISOString());
    } finally {
      setLoading(false);
    }
  };

  const byName = useMemo(() => {
    const m = {};
    indices.forEach(x => { m[x.name] = x; });
    return m;
  }, [indices]);

  const tspAsOf = tspFunds[0]?.asOf || '';

  return (
    <div className="panel p-5" data-testid="market-data-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <DollarSign size={14} className="text-[#1B2A4A]" />
          <h3 className="text-sm font-semibold text-[#1B2A4A]">Market Data</h3>
        </div>
        {asOf && <span className="text-[10px] text-[#9CA3AF] font-mono">Updated {fmtTS(asOf)}</span>}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
          <RefreshCw size={12} className="animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="market-indices-grid">
            {MARKET_ORDER.map(name => {
              const it = byName[name];
              if (!it) return null;
              const isUp = it.direction === 'up';
              const isDown = it.direction === 'down';
              const Arrow = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
              const colorCls = isUp ? 'text-emerald-700' : isDown ? 'text-red-700' : 'text-[#6B7280]';
              return (
                <div key={name} className="rounded border border-[#E5E7EB] bg-white px-2.5 py-2" data-testid={`market-index-${name.replace(/[^A-Za-z0-9]/g, '-')}`}>
                  <div className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280]">{name}</div>
                  <div className="font-mono text-sm text-[#1B2A4A] mt-0.5">
                    {typeof it.value === 'number' ? it.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : it.value}
                  </div>
                  <div className={`flex items-center gap-1 text-[10px] font-mono mt-0.5 ${colorCls}`}>
                    <Arrow size={9} />
                    <span>{(it.change || 0) >= 0 ? '+' : ''}{Number(it.change || 0).toFixed(2)}</span>
                    <span>({fmtPct(it.changePct)})</span>
                    {it.delayed && <span className="ml-1 text-amber-600 font-semibold">DELAYED</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* UniFed Core Core Funds */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">
                UniFed Core Core Funds
              </span>
              <span className="text-[10px] text-[#9CA3AF] font-mono" data-testid="tsp-asof">
                As of {tspAsOf || '—'} · Updated 7:00 PM ET daily
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="tsp-funds-grid">
              {tspFunds.map(f => {
                const isUp = f.dayChangePct > 0;
                const isDown = f.dayChangePct < 0;
                const Arrow = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
                const colorCls = isUp ? 'text-emerald-700' : isDown ? 'text-red-700' : 'text-[#6B7280]';
                return (
                  <div key={f.fund} className="rounded border border-[#E5E7EB] bg-[#F9FAFB] px-2.5 py-2" data-testid={`tsp-fund-${f.fund.replace(/\s+/g, '-')}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-[#1B2A4A]">{f.fund}</span>
                      {f.delayed && <span className="text-[8px] text-amber-600 font-semibold">DELAYED</span>}
                    </div>
                    <div className="font-mono text-sm text-[#1B2A4A] mt-0.5">${Number(f.price).toFixed(4)}</div>
                    <div className={`flex items-center gap-1 text-[10px] font-mono ${colorCls}`}>
                      <Arrow size={9} />{fmtPct(f.dayChangePct)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// News Feed — main page
// ──────────────────────────────────────────────────────────────
export const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [disclaimer, setDisclaimer] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [newCount, setNewCount] = useState(0);
  const [live, setLive] = useState(true);

  const [funds, setFunds] = useState([]);

  const knownIdsRef = useRef(new Set());
  const intervalRef = useRef(null);

  // Build fund lookup: name → pct
  const fundMap = useMemo(() => {
    const m = {};
    funds.forEach(f => { m[f.name] = f.allocation; });
    return m;
  }, [funds]);

  const loadParticipantFunds = useCallback(async () => {
    try {
      const r = await api.get('/participant/funds');
      setFunds(r.data?.funds || []);
    } catch { setFunds([]); }
  }, []);

  const loadArticles = useCallback(async (cat, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get('/news', { params: { category: cat, limit: 30 } });
      const items = r.data?.articles || [];
      setDisclaimer(r.data?.disclaimer || '');

      // Count new items (only after first load)
      if (!silent && knownIdsRef.current.size === 0) {
        // first load → seed known set, no badge
        items.forEach(a => knownIdsRef.current.add(a.id));
      } else if (silent) {
        let delta = 0;
        items.forEach(a => {
          if (!knownIdsRef.current.has(a.id)) delta += 1;
        });
        if (delta > 0) {
          setNewCount(c => {
            const next = c + delta;
            try { localStorage.setItem('news_unread_count', String(next)); } catch { /* ignore */ }
            return next;
          });
        }
        items.forEach(a => knownIdsRef.current.add(a.id));
      }
      setArticles(items);
      setLastRefresh(new Date().toISOString());
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Initial load + on category change
  useEffect(() => {
    knownIdsRef.current = new Set();
    setNewCount(0);
    try { localStorage.setItem('news_unread_count', '0'); } catch { /* ignore */ }
    loadArticles(category, false);
  }, [category, loadArticles]);

  useEffect(() => { loadParticipantFunds(); }, [loadParticipantFunds]);

  // Auto-refresh every 5 minutes (spec)
  useEffect(() => {
    if (!live) return;
    intervalRef.current = setInterval(() => {
      loadArticles(category, true);
    }, 5 * 60 * 1000);
    return () => clearInterval(intervalRef.current);
  }, [category, live, loadArticles]);

  const resetBadge = () => {
    setNewCount(0);
    try { localStorage.setItem('news_unread_count', '0'); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-4" data-testid="news-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans' }}>
            News &amp; Market Intelligence
          </h1>
          {newCount > 0 && (
            <button
              data-testid="news-new-badge"
              onClick={resetBadge}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-600 text-white text-[10px] font-semibold rounded-full hover:bg-emerald-700"
              title="Click to clear"
            >
              {newCount} new
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <button
            data-testid="news-live-toggle"
            onClick={() => setLive(v => !v)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded border ${live ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-gray-50 border-gray-200 text-[#6B7280]'}`}
          >
            <Radio size={11} className={live ? 'animate-pulse' : ''} />
            <span className="font-medium">{live ? 'Live' : 'Paused'}</span>
          </button>
          <button
            data-testid="news-refresh-btn"
            onClick={() => loadArticles(category, false)}
            className="flex items-center gap-1 px-2 py-1 rounded border border-[#D1D5DB] text-[#6B7280] hover:bg-gray-50"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
            <span className="font-medium">Refresh</span>
          </button>
          {lastRefresh && (
            <span className="text-[#9CA3AF] font-mono" data-testid="news-last-refresh">
              Last refresh {fmtTS(lastRefresh)}
            </span>
          )}
        </div>
      </div>

      {/* Market Data Panel */}
      <MarketDataPanel />

      {/* Category Filters */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="news-category-filters">
        {CATEGORIES.map(c => {
          const active = c === category;
          return (
            <button
              key={c}
              data-testid={`news-cat-${c.replace(/[^A-Za-z0-9]/g, '-')}`}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-medium border transition-colors ${
                active
                  ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]'
                  : 'bg-white text-[#1B2A4A] border-[#E5E7EB] hover:bg-gray-50'
              }`}
            >
              {c}
            </button>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded px-4 py-2 flex items-start gap-2" data-testid="news-disclaimer">
        <AlertTriangle size={12} className="text-amber-500 mt-0.5 shrink-0" />
        <p className="text-[10px] text-amber-700 leading-relaxed">
          {disclaimer || 'For informational purposes only. Not financial, tax, or investment advice.'}
        </p>
      </div>

      {/* Articles */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="panel p-4 animate-pulse">
              <div className="h-3 bg-gray-100 rounded w-3/4 mb-2" />
              <div className="h-2 bg-gray-100 rounded w-full mb-1" />
              <div className="h-2 bg-gray-100 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="panel p-12 text-center" data-testid="news-empty">
          <Info size={28} className="mx-auto text-[#D1D5DB] mb-3" />
          <p className="text-sm font-medium text-[#6B7280]">No articles in this category</p>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Try a different filter — articles refresh every 5 minutes from the background pipeline.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="news-articles-list">
          {articles.map((a, i) => (
            <ArticleCard key={a.id || i} article={a} fundMap={fundMap} index={i} />
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="text-[10px] text-[#9CA3AF] text-center font-mono" data-testid="news-footer">
        Feed auto-refreshes every 5 minutes · UniFed Core fund prices refresh at 7:00 PM ET
      </div>
    </div>
  );
};

// ─── Article Card ────────────────────────────────────────────
const ArticleCard = ({ article, fundMap, index }) => {
  const impactMeta = IMPACT_META[article.impact] || IMPACT_META.Neutral;
  const Icon = impactMeta.icon;

  // Relevance: find the first affected fund that the participant holds
  const relevantFund = (article.affectedFunds || []).find(fn => (fundMap[fn] || 0) > 0);
  const relevantPct = relevantFund ? fundMap[relevantFund] : 0;

  const displayTags = (() => {
    // Prioritize recognized tags, then alphabetical; cap at 4
    const raw = article.tags || [];
    const known = TAG_PRIORITY.filter(t => raw.some(r => (r || '').toLowerCase().includes(t.toLowerCase())));
    const other = raw.filter(r => !known.some(k => (r || '').toLowerCase().includes(k.toLowerCase())));
    return [...known, ...other].slice(0, 4);
  })();

  return (
    <article className="panel p-4 panel-hover" data-testid={`news-article-${index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Source + Impact + Tags */}
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-700" data-testid="article-source">
              {article.source}
            </span>
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded border ${impactMeta.cls}`}
              data-testid="article-impact"
            >
              <Icon size={10} />{article.impact}
            </span>
            {displayTags.map(t => (
              <span key={t} className="text-[9px] font-mono font-medium text-[#1B2A4A] bg-[#F0F1F3] px-1.5 py-0.5 rounded">
                {t}
              </span>
            ))}
          </div>

          {/* Headline */}
          <h3 className="text-sm font-medium text-[#1B2A4A] leading-snug mb-1" data-testid="article-headline">
            {article.url ? (
              <a href={article.url} target="_blank" rel="noopener noreferrer" className="hover:underline">{article.headline}</a>
            ) : article.headline}
          </h3>

          {/* Snippet (2 sentence max via line-clamp) */}
          {article.snippet && (
            <p className="text-xs text-[#6B7280] leading-relaxed line-clamp-2" data-testid="article-snippet">
              {article.snippet}
            </p>
          )}

          {/* Relevance Note */}
          {relevantFund && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 rounded px-2.5 py-1 text-[10px] text-indigo-800"
              data-testid="article-relevance"
            >
              <ChevronRight size={11} />
              Affects your <span className="font-semibold">{relevantFund}</span> ({relevantPct.toFixed(1)}% of your portfolio)
            </div>
          )}

          {/* Meta */}
          <div className="mt-2 flex items-center gap-3 text-[10px] text-[#9CA3AF] font-mono">
            <span className="flex items-center gap-1"><Clock size={9} />{fmtTS(article.timestamp)}</span>
            {article.categories?.length > 0 && <span>· {article.categories.slice(0, 2).join(' · ')}</span>}
          </div>
        </div>

        {article.url && (
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 p-2 hover:bg-gray-50 rounded transition-colors"
            data-testid="article-link"
          >
            <ExternalLink size={14} className="text-[#9CA3AF]" />
          </a>
        )}
      </div>
    </article>
  );
};
